import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { UserProfileService } from '../../services/user-profile.service';
import { IUserProfile, IUserProfileUpdate } from '../../interfaces/user-profile.interface';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';

interface Props {
    open: boolean;
    onClose: () => void;
    userId: string;
    displayName: string;
    onSaved?: () => void | Promise<void>;
}

interface EmploymentForm {
    phone: string;
    designation: string;
    joiningDate: string;
}

const emptyForm: EmploymentForm = { phone: '', designation: '', joiningDate: '' };

/**
 * HR edit of another employee's phone, designation and joining date (PROFILE_MANAGE). Until this
 * dialog existed the self-service profile page told employees to "ask your administrator" to change
 * these, but no admin screen could. Like EmployeeBankingDialog it re-reads the profile right before
 * the PUT so a concurrent edit to name/bank fields is never overwritten with dialog-open values.
 */
const EmployeeEmploymentDialog: React.FC<Props> = ({ open, onClose, userId, displayName, onSaved }) => {
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState<IUserProfile | null>(null);
    const [form, setForm] = useState<EmploymentForm>(emptyForm);

    useEffect(() => {
        if (!open || !userId) return;
        setLoading(true);
        UserProfileService.getProfileByUserId(userId)
            .then((p) => {
                setProfile(p);
                setForm({
                    phone: p.phone || '',
                    designation: p.designation || '',
                    joiningDate: p.joiningDate || '',
                });
            })
            .catch((err) => toast.showApiError(err, 'Failed to load employee details.'))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, userId]);

    const handleChange = (field: keyof EmploymentForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSave = async () => {
        if (!profile) return;
        setSaving(true);
        try {
            const current = await UserProfileService.getProfileByUserId(userId);
            let payload: IUserProfileUpdate = {
                email: current.email,
                firstName: current.firstName,
                lastName: current.lastName,
                phone: form.phone.trim() || current.phone,
            };
            // Blank means "no change" (designation / joining date are not clearable fields).
            if (form.designation.trim()) payload = { ...payload, designation: form.designation.trim() };
            if (form.joiningDate) payload = { ...payload, joiningDate: form.joiningDate };
            await UserProfileService.updateUserProfile(current.id, payload);
            toast.showSuccess('Employee details updated.');
            await onSaved?.();
            onClose();
        } catch (err) {
            toast.showApiError(err, 'Failed to update employee details.');
        } finally {
            setSaving(false);
        }
    };

    const saveWhy = saving ? null
        : loading ? 'The details are still loading. Wait a moment.'
        : !profile ? 'The details could not be loaded, so there is nothing to save. Close the window and open it again.'
        : null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" data-help-page="users.employment.page">
            <DialogTitle>Employment details — {displayName}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        data-help="users.employment.field.phone"
                        label="Phone"
                        fullWidth
                        value={form.phone}
                        onChange={handleChange('phone')}
                        disabled={loading}
                    />
                    <TextField
                        data-help="users.employment.field.designation"
                        label="Designation"
                        fullWidth
                        value={form.designation}
                        onChange={handleChange('designation')}
                        disabled={loading}
                    />
                    <TextField
                        data-help="users.employment.field.joiningDate"
                        label="Joining date"
                        type="date"
                        fullWidth
                        value={form.joiningDate}
                        onChange={handleChange('joiningDate')}
                        disabled={loading}
                        InputLabelProps={{ shrink: true }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving} data-help="users.employment.cancel">Cancel</Button>
                <Button onClick={handleSave} variant="contained" data-help="users.employment.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} disabled={loading || saving || !profile}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeEmploymentDialog;
